/**
 * Login Page
 * User authentication form with email/password
 */

import { useState, useEffect, useRef } from 'react'
import { useNavigate, useLocation, Link } from 'react-router-dom'
import { useAuth } from '../../contexts/AuthContext'
import { api, getErrorMessage } from '../../utils/api'
import { Button } from '../../components/ui'
import { PublicPageMeta } from '../../components/seo/PublicPageMeta'
import { buildApiNavigationUrl } from '../../utils/apiNavigation'
import styles from './Login.module.css'

// Timeout for login operations (30 seconds)
const LOGIN_TIMEOUT_MS = 30000

export const Login = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const { login, verifyMfa, isAuthenticated, isLoading } = useAuth()
  const timeoutRef = useRef<NodeJS.Timeout | null>(null)

  const getShopFromQuery = (): string | null => {
    try {
      const params = new URLSearchParams(location.search)
      return params.get('shop') || params.get('shop_domain') || params.get('shopDomain')
    } catch {
      return null
    }
  }
  
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  })
  const [rememberMe, setRememberMe] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [mfaRequired, setMfaRequired] = useState(false)
  const [mfaCode, setMfaCode] = useState('')

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
      }
    }
  }, [])

  /**
   * Redirect if already authenticated
   */
  useEffect(() => {
    if (!isLoading && isAuthenticated) {
      const shop = getShopFromQuery()
      if (shop) {
        window.location.href = buildApiNavigationUrl('/shopify/launch', { shop })
        return
      }

      // Check if user has pending subscription plan from signup
      const pendingPlan = sessionStorage.getItem('pendingPlan')
      if (pendingPlan) {
        try {
          const { tier, interval } = JSON.parse(pendingPlan)
          sessionStorage.removeItem('pendingPlan')
          
          // Redirect to checkout
          api.post('/subscription/checkout', { tier, interval })
            .then(response => {
              const provider = response.data?.data?.provider as string | undefined
              const checkoutUrl = response.data?.data?.checkoutUrl as string | undefined
              const redirectUrl = response.data?.data?.redirectUrl as string | undefined
              const urlToOpen = provider === 'shopify' ? redirectUrl : checkoutUrl

              if (response.data.success && urlToOpen) {
                window.location.href = urlToOpen
              } else {
                // Fallback to pricing page
                navigate('/pricing?upgrade=true')
              }
            })
            .catch((error: any) => {
              const reconnectUrl = error?.response?.data?.data?.reconnectUrl as string | undefined
              if (reconnectUrl) {
                try {
                  if (window.top && window.top !== window) {
                    window.top.location.href = reconnectUrl
                  } else {
                    window.location.href = reconnectUrl
                  }
                } catch {
                  window.location.href = reconnectUrl
                }
                return
              }
              // Fallback to pricing page
              navigate('/pricing?upgrade=true')
            })
          return
        } catch (error) {
          console.error('Error processing pending plan:', error)
        }
      }
      
      // Redirect to the page they were trying to access, or dashboard
      const from = (location.state as any)?.from?.pathname || '/dashboard'
      navigate(from, { replace: true })
    }
  }, [isAuthenticated, isLoading, navigate, location])

  /**
   * Handle input changes
   */
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: value
    }))
    // Clear error when user starts typing
    if (error) setError(null)
  }

  /**
   * Handle form submission
   */
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)
    setIsSubmitting(true)

    // Clear any existing timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current)
    }

    // Basic validation
    if (!mfaRequired) {
      if (!formData.email || !formData.password) {
        setError('Please enter both email and password')
        setIsSubmitting(false)
        return
      }
    } else {
      const cleaned = mfaCode.replace(/\s/g, '')
      if (!cleaned || cleaned.length < 6) {
        setError('Please enter your 6-digit authentication code')
        setIsSubmitting(false)
        return
      }
    }

    // Set a timeout to prevent infinite loading state
    timeoutRef.current = setTimeout(() => {
      if (isSubmitting) {
        setIsSubmitting(false)
        setError('Login request timed out. Please check your connection and try again.')
      }
    }, LOGIN_TIMEOUT_MS)

    try {
      if (!mfaRequired) {
        const result = await login(formData.email, formData.password, rememberMe)
        if (result?.mfaRequired) {
          setMfaRequired(true)
          setIsSubmitting(false)
          if (timeoutRef.current) {
            clearTimeout(timeoutRef.current)
          }
          return
        }
      } else {
        await verifyMfa(mfaCode, rememberMe)
      }

      // Clear timeout on success
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
      }

      // If a Shopify shop param is present, redirect back through /launch so the
      // cross-org check fires now that the user has a valid session. This must
      // happen BEFORE the generic navigate() to avoid a race where the Login
      // component unmounts before the shopify-redirect useEffect can fire.
      const shopAfterLogin = getShopFromQuery()
      if (shopAfterLogin) {
        window.location.href = buildApiNavigationUrl('/shopify/launch', { shop: shopAfterLogin })
        return
      }

      // Redirect to dashboard on successful login
      const from = (location.state as any)?.from?.pathname || '/dashboard'
      navigate(from, { replace: true })
    } catch (err: any) {
      // Clear timeout on error
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
      }
      const errorMessage = getErrorMessage(err)
      setError(errorMessage)
    } finally {
      setIsSubmitting(false)
    }
  }

  // Show loading state while checking authentication
  if (isLoading) {
    return (
      <>
        <PublicPageMeta
          title="Sign in"
          description="Sign in to TCGEasy to manage inventory, pricing sync, and your connected stores."
        />
      <div className={styles.container}>
        <div className={styles.card}>
          <div className={styles.header}>
            <h1 className={styles.title}>TCGEasy</h1>
            <p className={styles.subtitle}>Checking authentication...</p>
          </div>
          <div className={styles.loadingSpinner}>
            <svg className={styles.spinner} viewBox="0 0 24 24" fill="none">
              <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeDasharray="31.416" strokeDashoffset="10" />
            </svg>
          </div>
        </div>
      </div>
      </>
    )
  }

  return (
    <>
      <PublicPageMeta
        title="Sign in"
        description="Sign in to TCGEasy to manage inventory, pricing sync, and your connected stores."
      />
    <div className={styles.container}>
      <div className={styles.card}>
        <div className={styles.header}>
          <h1 className={styles.title}>TCGEasy</h1>
          <p className={styles.subtitle}>Sign in to your account</p>
        </div>

        <form onSubmit={handleSubmit} className={styles.form}>
          {error && (
            <div className={styles.alert}>
              <svg 
                className={styles.alertIcon}
                fill="none" 
                stroke="currentColor" 
                viewBox="0 0 24 24"
              >
                <path 
                  strokeLinecap="round" 
                  strokeLinejoin="round" 
                  strokeWidth={2} 
                  d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" 
                />
              </svg>
              <span>{error}</span>
            </div>
          )}

          {!mfaRequired ? (
            <>
              <div className={styles.formGroup}>
                <label htmlFor="email" className={styles.label}>
                  Email Address
                </label>
                <input
                  id="email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  required
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="you@example.com"
                  disabled={isSubmitting}
                  className={styles.input}
                />
              </div>

              <div className={styles.formGroup}>
                <label htmlFor="password" className={styles.label}>
                  Password
                </label>
                <input
                  id="password"
                  name="password"
                  type="password"
                  autoComplete="current-password"
                  required
                  value={formData.password}
                  onChange={handleChange}
                  placeholder="••••••••"
                  disabled={isSubmitting}
                  className={styles.input}
                />
              </div>

              <div className={styles.formOptions}>
                <label className={styles.checkboxLabel}>
                  <input
                    type="checkbox"
                    checked={rememberMe}
                    onChange={(e) => setRememberMe(e.target.checked)}
                    disabled={isSubmitting}
                    className={styles.checkbox}
                  />
                  <span>Remember me</span>
                </label>

                <Link to="/forgot-password" className={styles.forgotLink}>
                  Forgot password?
                </Link>
              </div>
            </>
          ) : (
            <>
              <div className={styles.formGroup}>
                <label htmlFor="mfa" className={styles.label}>
                  Authentication Code
                </label>
                <input
                  id="mfa"
                  name="mfa"
                  type="text"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  value={mfaCode}
                  onChange={(e) => setMfaCode(e.target.value)}
                  placeholder="123456"
                  disabled={isSubmitting}
                  className={styles.input}
                />
                <p style={{ marginTop: '8px', fontSize: '0.85rem', opacity: 0.85, color: 'var(--text-muted, #666)' }}>
                  You can enter a 6-digit code from your authenticator app or a one-time recovery code from Settings.
                </p>
              </div>
            </>
          )}

          <Button
            type="submit"
            variant="primary"
            size="large"
            fullWidth
            isLoading={isSubmitting}
          >
            {mfaRequired ? 'Verify code' : 'Sign in'}
          </Button>
        </form>

        <div className={styles.footer}>
          <div className={styles.signupPrompt}>
            <span className={styles.signupText}>Don't have an account?</span>
            <Link to="/signup" className={styles.signupLink}>Create one</Link>
          </div>

          <p className={styles.footerText}>
            TCGEasy - Trading Card Inventory Management
          </p>

          <div className={styles.legalLinks} aria-label="Legal links">
            <Link to="/tos" className={styles.legalLink}>Terms</Link>
            <span className={styles.legalSeparator} aria-hidden="true">•</span>
            <Link to="/privacy" className={styles.legalLink}>Privacy</Link>
          </div>
        </div>
      </div>
    </div>
    </>
  )
}

export default Login
